import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchListeners, createListener, startListener, stopListener, deleteListener } from '../store/listenersSlice';
import Modal from '../components/Modal';

function Listeners() {
  const dispatch = useDispatch();
  const { listeners, loading } = useSelector((state) => state.listeners);
  const [showCreate, setShowCreate] = useState(false);
  const [newListener, setNewListener] = useState({
    name: '',
    listener_type: 'https',
    bind_address: '0.0.0.0',
    bind_port: 443,
    description: ''
  });

  useEffect(() => {
    dispatch(fetchListeners());
  }, [dispatch]);

  const handleCreate = async () => {
    await dispatch(createListener(newListener));
    setShowCreate(false);
    setNewListener({
      name: '',
      listener_type: 'https',
      bind_address: '0.0.0.0',
      bind_port: 443,
      description: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'var(--neon-green)';
      case 'stopped': return 'var(--text-muted)';
      case 'error': return 'var(--neon-red)';
      default: return 'var(--text-secondary)';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'https': return '🔒';
      case 'http': return '🌐';
      case 'dns': return '📡';
      case 'tcp': return '🔌';
      default: return '📶';
    }
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Listeners</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New Listener
          </button>
        </div>
      </header>

      <div className="main-body">
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
          gap: 'var(--spacing-lg)' 
        }}>
          {listeners.map((listener) => (
            <div key={listener.listener_id} className="card">
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  <span>{getTypeIcon(listener.listener_type)}</span>
                  <h3 className="card-title" style={{ margin: 0 }}>{listener.name}</h3>
                </div>
                <span style={{ 
                  color: getStatusColor(listener.status),
                  fontSize: '12px',
                  textTransform: 'uppercase'
                }}>
                  ● {listener.status}
                </span>
              </div>
              <div className="card-body">
                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '4px' }}>
                    BIND ADDRESS
                  </div>
                  <code style={{ color: 'var(--neon-cyan)' }}>
                    {listener.listener_type}://{listener.bind_address}:{listener.bind_port}
                  </code>
                </div>
                
                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '4px' }}>
                    ACTIVE AGENTS
                  </div>
                  <span style={{ color: 'var(--neon-green)', fontWeight: 600 }}>
                    {listener.active_agents || 0}
                  </span>
                </div>

                {listener.description && (
                  <div style={{ 
                    color: 'var(--text-muted)', 
                    fontSize: '12px',
                    marginBottom: 'var(--spacing-md)'
                  }}>
                    {listener.description}
                  </div>
                )}

                <div style={{ 
                  display: 'flex', 
                  gap: 'var(--spacing-sm)', 
                  marginTop: 'var(--spacing-md)',
                  paddingTop: 'var(--spacing-md)',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  {listener.status === 'running' ? (
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => dispatch(stopListener(listener.listener_id))}
                    >
                      ⏹ Stop
                    </button>
                  ) : (
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => dispatch(startListener(listener.listener_id))}
                    >
                      ▶ Start
                    </button>
                  )}
                  <button 
                    className="btn btn-sm btn-danger"
                    onClick={() => dispatch(deleteListener(listener.listener_id))}
                    disabled={listener.status === 'running'}
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            </div>
          ))}

          {listeners.length === 0 && !loading && (
            <div className="card">
              <div className="card-body" style={{ 
                textAlign: 'center', 
                padding: '60px',
                color: 'var(--text-muted)'
              }}>
                <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-md)' }}>📡</div>
                <p>No listeners configured</p>
                <button 
                  className="btn btn-primary" 
                  style={{ marginTop: 'var(--spacing-md)' }}
                  onClick={() => setShowCreate(true)}
                >
                  Create First Listener
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Listener Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create New Listener"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleCreate}>
              Create Listener
            </button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            type="text"
            className="form-input"
            value={newListener.name}
            onChange={(e) => setNewListener({ ...newListener, name: e.target.value })}
            placeholder="e.g., HTTPS Primary"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={newListener.listener_type}
            onChange={(e) => setNewListener({ ...newListener, listener_type: e.target.value })}
          >
            <option value="https">HTTPS</option>
            <option value="http">HTTP</option>
            <option value="dns">DNS</option>
            <option value="tcp">TCP</option>
          </select>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--spacing-md)' }}>
          <div className="form-group">
            <label className="form-label">Bind Address</label>
            <input
              type="text"
              className="form-input"
              value={newListener.bind_address}
              onChange={(e) => setNewListener({ ...newListener, bind_address: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Port</label>
            <input
              type="number"
              className="form-input"
              value={newListener.bind_port}
              onChange={(e) => setNewListener({ ...newListener, bind_port: parseInt(e.target.value) })}
            />
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label">Description (Optional)</label>
          <textarea
            className="form-input"
            value={newListener.description}
            onChange={(e) => setNewListener({ ...newListener, description: e.target.value })}
            rows={2}
          />
        </div>
      </Modal>
    </>
  );
}

export default Listeners;
